import { useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import { useWorkspaceId } from "src/contexts/global/ApolloSettingsContext";
import { useDesignOptionsEnabled } from "src/pages/Designs/overlay/designOptionsEnabled";
import { getDesignOptions as getDesignOptionsQuery } from "src/pages/Designs/overlay/designOption.graphql";
import useIsDesignWorkspace from "src/pages/Workspaces/components/WorkspaceAccess/useIsDesignWorkspace";
import { listDesignWorkspaceOptions as listDesignWorkspaceOptionsQuery } from "src/pages/Designs/designs.graphql";

export type DesignWorkspaceOption = {
  id: string;
  name: string;
  isTrunk: boolean;
};

export type DesignWorkspaceDesign = {
  id: string;
  designWorkspaceId?: string | null;
  baselineDataDate?: string | null;
  startDate?: string | null;
  targetImplementationDate?: string | null;
  workflowStage?: string | null;
  options?: DesignWorkspaceOption[];
};

/** ponytail: trunk row is DB-backed only after the first overlay create */
const implicitTrunkOption = (): DesignWorkspaceOption => ({
  id: "",
  name: "Current",
  isTrunk: true,
});

const withImplicitTrunk = (
  design: DesignWorkspaceDesign | null | undefined,
  options: DesignWorkspaceOption[] | undefined
): DesignWorkspaceOption[] => {
  if (!design) return [];
  const fetched = options ?? [];
  return fetched.length === 0 ? [implicitTrunkOption()] : fetched;
};

type ListDesignWorkspaceOptionsData = {
  designs?: DesignWorkspaceDesign[];
};

type GetDesignOptionsData = {
  design?: {
    options?: DesignWorkspaceOption[];
  } | null;
};

export type DesignWorkspaceOptionsOverride = {
  design: DesignWorkspaceDesign | null | undefined;
  refetch: () => Promise<unknown>;
};

/**
 * By default resolves the design (and its options) that belongs to the
 * current workspace. Design metadata comes from listDesigns; options are
 * fetched only for the matching design via getDesign so FORBIDDEN on other
 * sandboxes cannot kill the switcher. When `override` is supplied the
 * hook instead reflects an already-fetched `design` (and reuses the
 * caller's own `refetch`), skipping both queries entirely.
 */
export const useDesignWorkspaceOptions = (
  override?: DesignWorkspaceOptionsOverride
) => {
  const workspaceId = useWorkspaceId();
  const enabled = useDesignOptionsEnabled();
  const { isDesignWorkspace, loading: isDesignWorkspaceLoading } =
    useIsDesignWorkspace(workspaceId);

  const queryEnabled =
    enabled &&
    isDesignWorkspace &&
    !isDesignWorkspaceLoading &&
    Boolean(workspaceId) &&
    !override;

  const { data, refetch: listRefetch } =
    useQuery<ListDesignWorkspaceOptionsData>(listDesignWorkspaceOptionsQuery, {
      skip: !queryEnabled,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    });

  const matchedDesign = useMemo(() => {
    if (override) return override.design;
    const designs = data?.designs ?? [];
    return designs.find((item) => item?.designWorkspaceId === workspaceId);
  }, [data, workspaceId, override]);

  const designId = matchedDesign?.id;

  const { data: optionsData, refetch: optionsRefetch } =
    useQuery<GetDesignOptionsData>(getDesignOptionsQuery, {
      skip: !queryEnabled || !designId,
      variables: { id: designId ?? "" },
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    });

  const optionsLoading =
    queryEnabled && Boolean(designId) && optionsData === undefined;

  const design = useMemo(() => {
    if (override) {
      if (!override.design) return override.design;
      return {
        ...override.design,
        options: withImplicitTrunk(override.design, override.design.options),
      };
    }
    if (!matchedDesign) return undefined;
    return {
      ...matchedDesign,
      options: withImplicitTrunk(matchedDesign, optionsData?.design?.options),
    };
  }, [override, matchedDesign, optionsData]);

  const options: DesignWorkspaceOption[] = design?.options ?? [];

  const refetch = useCallback(async () => {
    if (override) {
      return override.refetch();
    }
    const listResult = await listRefetch();
    const refreshedDesignId =
      listResult?.data?.designs?.find(
        (item) => item?.designWorkspaceId === workspaceId
      )?.id ?? designId;
    if (refreshedDesignId) {
      await optionsRefetch({ id: refreshedDesignId });
    }
  }, [override, listRefetch, optionsRefetch, workspaceId, designId]);

  return {
    enabled,
    isDesignWorkspace,
    workspaceId,
    design,
    options,
    optionsLoading,
    refetch,
  };
};

export default useDesignWorkspaceOptions;
